import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { patchFehlerText } from '@/hooks/usePatchBestellung';

type Eingabe = { id: number; bestellnr: string; zustand_code: string };
type Antwort = { unchanged?: boolean; aus_archiv_zurueck?: boolean };

/**
 * Zustand einer Bestellung setzen — vom Brett aus (A12.7).
 *
 * DERSELBE ENDPUNKT WIE IN DER BESTELLUNG
 *   `/bestellungen/zustand.php`, kein zweiter Weg. Dort haengen die
 *   Zustands-Automatik, der Verlaufseintrag und seit A12.13 das
 *   Zurueckholen aus dem Archiv. Ein eigener Endpunkt fuers Brett
 *   haette all das noch einmal — und irgendwann anders.
 *
 * WAS DIESER WEG NICHT TUT
 *   Keine Sendung anlegen. In der Bestellung oeffnet „bereit" den
 *   Sendungsdialog; hier nicht. Wer am Brett steht, sortiert — er hat
 *   das Buch nicht in der Hand und kann nicht sagen, welche Positionen
 *   in welches Paket gehen. Der Zustand wird gesetzt, die Sendung
 *   entsteht in der Bestellung.
 */
export function useZustandSetzen() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation<Antwort, Error, Eingabe>({
    mutationFn: (v) =>
      apiFetch<Antwort>('/bestellungen/zustand.php', {
        method: 'POST',
        body: JSON.stringify({ id: v.id, zustand_code: v.zustand_code })
      }),
    onError: (err) => toast('error', patchFehlerText(err)),
    onSuccess: (resp) => {
      if (resp.unchanged === true) {
        toast('success', 'Nichts geändert.');
        return;
      }
      // Das Zurueckholen aus dem Archiv passiert still im Server (A12.13).
      // Wer es nicht erfaehrt, sucht die Bestellung anschliessend im
      // Archiv, wo sie nicht mehr ist.
      toast(
        'success',
        resp.aus_archiv_zurueck === true
          ? 'Zustand gesetzt und aus dem Archiv zurückgeholt.'
          : 'Zustand gesetzt.'
      );
    },
    onSettled: (_d, _e, v) => {
      qc.invalidateQueries({ queryKey: ['bestellungen'] });
      qc.invalidateQueries({ queryKey: ['bestellung', v.bestellnr] });
    }
  });
}
